import { Divider, Paper, Popover, Tooltip, Typography } from "@mui/material";
import i18n from "i18next";
import { faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";
import { formatMoneyWithCurrency } from "src/utils/numberUtils.ts";
import { ConfigurationCheckpointEntry } from "src/components/views/erp/configurator/configuratorForm/checkpoints/configurationCheckpoints.ts";

export interface ConfigurationCheckpointLogPopoutProps {
	anchorEl: HTMLElement | null;
	open: boolean;
	onClose: () => void;
	entries: ConfigurationCheckpointEntry[];
	hasPendingChanges: boolean;
}

export const ConfigurationCheckpointLogPopout = ({
	anchorEl,
	open,
	onClose,
	entries,
	hasPendingChanges,
}: ConfigurationCheckpointLogPopoutProps) => {
	return (
		<Popover
			open={open}
			anchorEl={anchorEl}
			onClose={onClose}
			anchorOrigin={{ vertical: "top", horizontal: "left" }}
			transformOrigin={{ vertical: "bottom", horizontal: "left" }}
		>
			<Paper sx={{ p: 2, maxWidth: 600, minWidth: 280 }}>
				<Typography variant={"subtitle2"} sx={{ mb: 1 }}>
					{i18n.t("checkpoint_log")}
				</Typography>
				{entries.length === 0 ?
					<Typography variant={"body2"} color={"text.secondary"}>
						{i18n.t("no_checkpoints_yet")}
					</Typography>
				:	<VerticalBox sx={{ gap: 1 }}>
						{entries.map((entry, index) => (
							<CheckpointEntryRow key={index} entry={entry} index={index} />
						))}
					</VerticalBox>
				}
				{hasPendingChanges && (
					<>
						<Divider sx={{ my: 1 }} />
						<HorizontalBox sx={{ alignItems: "center", gap: 0.5 }}>
							<Tooltip title={i18n.t("changes_since_last_checkpoint")}>
								<FontAwesomeIcon icon={faTriangleExclamation} color={"orange"} />
							</Tooltip>
							<Typography variant={"caption"} color={"warning.main"}>
								{i18n.t("changes_since_last_checkpoint")}
							</Typography>
						</HorizontalBox>
					</>
				)}
			</Paper>
		</Popover>
	);
};

interface CheckpointEntryRowProps {
	entry: ConfigurationCheckpointEntry;
	index: number;
}

const CheckpointEntryRow = ({ entry, index }: CheckpointEntryRowProps) => {
	const price = entry.price;

	return (
		<VerticalBox sx={{ gap: 0.25 }}>
			<HorizontalBox sx={{ alignItems: "baseline", gap: 0.5 }}>
				<Typography variant={"caption"} color={"text.secondary"}>
					{`#${index + 1}`}
				</Typography>
				{price?.salesPriceDiff != null && (
					<Typography
						variant={"caption"}
						color={price.salesPriceDiff >= 0 ? "success.main" : "error.main"}
						sx={{ fontWeight: "bold" }}
					>
						{`${price.salesPriceDiff >= 0 ? "+" : ""}${formatMoneyWithCurrency(price.salesPriceDiff, price.sellingCurrencyCode)}`}
					</Typography>
				)}
				{price?.salesPriceWithVat != null && (
					<Typography variant={"caption"} color={"text.secondary"}>
						{`(${i18n.t("total_price")}: ${formatMoneyWithCurrency(price.salesPriceWithVat, price.sellingCurrencyCode)})`}
					</Typography>
				)}
			</HorizontalBox>
			{entry.description && (
				<Typography variant={"body2"} sx={{ whiteSpace: "pre-line" }}>
					{entry.description}
				</Typography>
			)}
			<Divider />
		</VerticalBox>
	);
};
